import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Bell, CheckCircle2, FileSearch, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TeamManager } from '@/features/team/components/TeamManager';
import { InviteModal } from '@/features/team/components/InviteModal';
import {
  useTeam as useTeamDetail,
  usePendingInvitations,
  useAcceptInvitation,
} from '@/features/team/hooks/useTeam';
import { useReviews, useCreateReview } from '@/features/review/hooks/useReview';

export function Team() {
  const navigate = useNavigate();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [inviteTeamId, setInviteTeamId] = useState<string | null>(null);

  const { data: selectedTeam } = useTeamDetail(selectedTeamId ?? undefined);
  const { data: pendingInvitations } = usePendingInvitations();
  const acceptInvitation = useAcceptInvitation();
  const { data: reviews } = useReviews(selectedTeamId ?? undefined);
  const createReview = useCreateReview();

  const handleAcceptInvitation = async (teamId: string) => {
    try {
      await acceptInvitation.mutateAsync(teamId);
      toast.success('Invitation accepted');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to accept invitation'
      );
    }
  };

  const pendingCount = pendingInvitations?.length ?? 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100">
          <Users className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Teams</h1>
          <p className="text-sm text-slate-500">
            Collaborate on systematic reviews with your team
          </p>
        </div>
      </div>

      <Tabs defaultValue="teams">
        <TabsList>
          <TabsTrigger value="teams">My Teams</TabsTrigger>
          <TabsTrigger value="reviews" className="flex items-center gap-1.5">
            <FileSearch className="h-3.5 w-3.5" />
            Reviews
            {reviews && reviews.length > 0 && (
              <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                {reviews.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-1.5">
            Invitations
            {pendingCount > 0 && (
              <Badge
                variant="destructive"
                className="h-5 min-w-5 px-1.5 text-xs"
              >
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Teams tab */}
        <TabsContent value="teams" className="mt-6">
          <TeamManager
            selectedTeamId={selectedTeamId}
            onSelectTeam={setSelectedTeamId}
            onInvite={(teamId) => setInviteTeamId(teamId)}
          />
        </TabsContent>

        {/* Reviews tab */}
        <TabsContent value="reviews" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Reviews</h2>
              {selectedTeamId && (
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const review = await createReview.mutateAsync({
                        team_id: selectedTeamId,
                        name: `Review ${new Date().toLocaleDateString()}`,
                      });
                      navigate(`/review/${review.id}`);
                    } catch {
                      // Error handled by mutation
                    }
                  }}
                  disabled={createReview.isPending}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Review
                </Button>
              )}
            </div>

            {!selectedTeamId ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <FileSearch className="h-10 w-10 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">
                    Select a team to see its reviews
                  </p>
                </CardContent>
              </Card>
            ) : !reviews?.length ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <FileSearch className="h-10 w-10 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">
                    No reviews yet. Create one to start screening.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reviews.map((review) => (
                  <Card
                    key={review.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/review/${review.id}`)}
                  >
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">
                        {review.name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {review.review_mode.replace('_', ' ')}
                        </Badge>
                        <Badge
                          variant={review.status === 'completed' ? 'default' : 'secondary'}
                          className="text-xs capitalize"
                        >
                          {review.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    {review.description && (
                      <CardContent className="pt-0">
                        <p className="text-xs text-slate-500 line-clamp-1">
                          {review.description}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Invitations tab */}
        <TabsContent value="invitations" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-slate-400" />
              <h2 className="text-lg font-semibold text-slate-900">
                Pending Invitations
              </h2>
            </div>

            {!pendingInvitations?.length ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <CheckCircle2 className="h-10 w-10 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">
                    No pending invitations
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pendingInvitations.map((invitation) => (
                  <Card key={invitation.id}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">
                        {invitation.team?.name ?? 'Unknown Team'}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {invitation.role}
                      </Badge>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between">
                      <p className="text-xs text-slate-500">
                        Invited{' '}
                        {new Date(invitation.invited_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            handleAcceptInvitation(invitation.team_id)
                          }
                          disabled={acceptInvitation.isPending}
                        >
                          {acceptInvitation.isPending
                            ? 'Accepting...'
                            : 'Accept'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Invite Modal */}
      {inviteTeamId && (
        <InviteModal
          teamId={inviteTeamId}
          teamName={selectedTeam?.name ?? 'Team'}
          open={inviteTeamId !== null}
          onOpenChange={(open) => {
            if (!open) setInviteTeamId(null);
          }}
        />
      )}
    </div>
  );
}
